export type RandomSeed = number | string

const UINT32_RANGE = 0x1_0000_0000
const LCG_MULTIPLIER = 1_664_525
const LCG_INCREMENT = 1_013_904_223
const FNV_OFFSET_BASIS = 2_166_136_261
const FNV_PRIME = 16_777_619

/**
 * Converts supported project seeds into a stable unsigned 32-bit value.
 * String hashing intentionally operates on UTF-16 code units so the result is
 * identical in every JavaScript runtime without an encoder allocation.
 */
export function normalizeSeed(seed: RandomSeed): number {
  if (typeof seed === 'number') {
    if (!Number.isFinite(seed)) {
      throw new RangeError('Random seed must be a finite number')
    }

    return Math.trunc(seed) >>> 0
  }

  let hash = FNV_OFFSET_BASIS
  for (let index = 0; index < seed.length; index += 1) {
    hash = Math.imul(hash ^ seed.charCodeAt(index), FNV_PRIME)
  }

  return hash >>> 0
}

/** A small allocation-free PRNG whose sequence is part of the V2 file format. */
export class SeededRandom {
  private state: number

  constructor(seed: RandomSeed) {
    this.state = normalizeSeed(seed)
  }

  nextUint32(): number {
    this.state = (Math.imul(LCG_MULTIPLIER, this.state) + LCG_INCREMENT) >>> 0
    return this.state
  }

  nextFloat(): number {
    return this.nextUint32() / UINT32_RANGE
  }
}
